import type { ShipFrom, Location as StoreLocation } from "@hobbyshopify/backend-client";
import { useObserver, useSubtree } from "subtree.js/react";
import { LocationEditor } from "../../../../components/LocationFields";
import type { FormStatus } from "../../../../core/primitives/load-status";
import { formatLocationAddress } from "../../../../core/primitives/location-form";
import {
  isAwaitingGeocode,
  isMappable,
  takesInPersonPayments,
} from "../../../../core/repositories/locations-repository";
import { LocationsSectionActions, LocationsSectionState } from "./locations-section-model";

function MapBadge({ location }: { location: StoreLocation }) {
  const mapped = isMappable(location);
  const searching = isAwaitingGeocode(location);

  // Three states, not two: an address that never resolves must not sit on
  // "Finding on map…" forever pretending something is still happening.
  const badge = mapped
    ? { label: "On the map", className: "badge-accent" }
    : searching
      ? { label: "Finding on map…", className: "badge-warn" }
      : { label: "Not on the map", className: "badge-neutral" };

  return <span className={`badge shrink-0 ${badge.className}`}>{badge.label}</span>;
}

/** The server names the columns; a merchant is looking at labelled boxes. */
const FIELD_LABELS: Record<string, string> = {
  address1: "a street address",
  city: "a town or city",
  province: "a state or province",
  zip: "a postal code",
  countryCode: "a country",
};

const describeMissing = (missing: string[]) => missing.map((field) => FIELD_LABELS[field] ?? field).join(", ");

/**
 * Which location parcels leave from — the return address printed on a shipping
 * label.
 *
 * Only asked once there is something to ask: a shop with one location has no
 * choice to make and is told what will happen instead. What a merchant cannot be
 * left with is silence, because the failure this prevents is invisible until a
 * label refuses to print.
 */
function ShipFromPicker({
  shipFrom,
  locations,
  form,
  onChange,
}: {
  shipFrom: ShipFrom | null;
  locations: readonly StoreLocation[];
  form: FormStatus;
  onChange: (locationId: string | null) => void;
}) {
  if (!shipFrom || locations.length === 0) return null;

  const missingFor = new Map(shipFrom.options.map((option) => [option.locationId, option.missing]));
  // Complete, but somewhere no carrier collects from. A different sentence from
  // "needs zip": there is no box to go and fill in, and nothing is broken —
  // those shops post their own parcels.
  const blockedFor = new Map(shipFrom.options.map((option) => [option.locationId, option.blocked]));
  const nameOf = (id: string | null) => locations.find((location) => location.id === id)?.name ?? null;
  const resolvedName = nameOf(shipFrom.resolvedLocationId);
  const selectedName = nameOf(shipFrom.selectedLocationId);

  const status = resolvedName ? (
    <>
      Labels return to <span className="text-white">{resolvedName}</span>
      {shipFrom.selectedLocationId === null && locations.length === 1 ? " — your only location." : "."}
    </>
  ) : selectedName && blockedFor.get(shipFrom.selectedLocationId ?? "") ? (
    blockedFor.get(shipFrom.selectedLocationId ?? "")
  ) : selectedName ? (
    <>
      {selectedName} needs {describeMissing(missingFor.get(shipFrom.selectedLocationId ?? "") ?? [])} before a label can
      be printed.
    </>
  ) : (
    <>Choose where parcels leave from. Until you do, labels can't be printed.</>
  );

  return (
    <div className="mt-4 border-t border-line pt-4">
      <p className="text-[13px] font-medium text-white">Ships from</p>
      <p className="mt-1 text-xs leading-snug text-muted">
        The return address on shipping labels for live break orders.
      </p>

      {locations.length > 1 && (
        <select
          aria-label="Location parcels ship from"
          className="field mt-2.5"
          value={shipFrom.selectedLocationId ?? ""}
          disabled={form.busy}
          onChange={(event) => onChange(event.target.value || null)}
        >
          <option value="">Not set</option>
          {locations.map((location) => {
            const missing = missingFor.get(location.id) ?? [];
            const blocked = blockedFor.get(location.id) ?? null;
            return (
              <option key={location.id} value={location.id} disabled={missing.length > 0 || blocked !== null}>
                {location.name}
                {missing.length > 0
                  ? ` — needs ${describeMissing(missing)}`
                  : blocked
                    ? " — outside the US, no carrier labels"
                    : ""}
              </option>
            );
          })}
        </select>
      )}

      <p className={`mt-2 text-xs leading-snug ${resolvedName ? "text-muted" : "text-warn"}`}>{status}</p>
      {form.error && <p className="note note-danger mt-2 text-danger">{form.error}</p>}
    </div>
  );
}

/**
 * Whether this branch can take a card in person, and the one action that changes
 * it.
 *
 * Stripe will not register a reader — a Tap to Pay phone included — without a
 * Terminal location, so a branch without one fails at the till with a customer
 * waiting. Set up here, in advance, beside the address it is created from.
 *
 * The button needs an address for the same reason: Stripe requires a street and
 * a country on the location, so an unaddressed branch is told what is missing
 * rather than being offered a button that would fail.
 */
function TerminalControl({
  location,
  busy,
  error,
  onEnable,
}: {
  location: StoreLocation;
  busy: boolean;
  error: string | null;
  onEnable: () => void;
}) {
  if (takesInPersonPayments(location)) {
    return <span className="badge badge-accent shrink-0">Takes cards</span>;
  }

  const addressable = Boolean(location.address1 && location.countryCode);

  return (
    <div className="flex shrink-0 flex-col items-end gap-1">
      <button
        type="button"
        disabled={!addressable || busy}
        onClick={onEnable}
        className="btn-ghost"
        title={addressable ? undefined : "Add a street address and country first"}
      >
        {busy ? "Setting up…" : "Set up card reader"}
      </button>
      {error && <span className="text-xs text-danger">{error}</span>}
    </div>
  );
}

export function LocationsSection() {
  const state = useSubtree(LocationsSectionState);
  const actions = useSubtree(LocationsSectionActions);
  const observer = useObserver();
  const locations = observer.watch(state.locations);
  const status = observer.watch(state.status);
  const shipFrom = observer.watch(state.shipFrom);
  const countries = observer.watch(state.countries);
  const countriesStatus = observer.watch(state.countriesStatus);
  const adding = observer.watch(state.adding);
  const addDraft = observer.watch(state.addDraft);
  const addRegions = observer.watch(state.addRegions);
  const addErrors = observer.watch(state.addErrors);
  const addForm = observer.watch(state.addForm);
  const editingId = observer.watch(state.editingId);
  const editDraft = observer.watch(state.editDraft);
  const editRegions = observer.watch(state.editRegions);
  const editErrors = observer.watch(state.editErrors);
  const editForm = observer.watch(state.editForm);
  const shipFromForm = observer.watch(state.shipFromForm);
  const terminalBusyId = observer.watch(state.terminalBusyId);
  const terminalError = observer.watch(state.terminalError);

  const serverError = addForm.error ?? editForm.error;
  const isLoading = locations.length === 0 && (status.kind === "idle" || status.kind === "loading");

  return (
    <section className="panel p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-white">Locations</h2>
          <p className="mt-1 text-sm text-muted">
            Where you sell. An address puts the shop on the map customers use to find you.
          </p>
        </div>
        {!adding && (
          <button type="button" onClick={actions.startAdd} className="btn-secondary btn-sm shrink-0">
            Add location
          </button>
        )}
      </div>

      {serverError && <p className="note note-danger mt-4 text-danger">{serverError}</p>}

      {isLoading ? (
        <p className="mt-4 text-sm text-muted">Loading locations…</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-2.5">
          {locations.map((location) => {
            const address = formatLocationAddress(location);
            return (
              <li key={location.id}>
                {editingId === location.id ? (
                  <LocationEditor
                    draft={editDraft}
                    errors={editErrors}
                    countries={countries}
                    countriesStatus={countriesStatus}
                    regions={editRegions}
                    onChange={actions.setEditField}
                    idPrefix={`edit-${location.id}`}
                    submitLabel="Save location"
                    submittingLabel="Saving…"
                    busy={editForm.busy}
                    onSubmit={() => void actions.saveEdit()}
                    onCancel={actions.cancelEdit}
                  />
                ) : (
                  <div className="flex items-center gap-3 rounded-row border border-line px-3.5 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-white">{location.name}</p>
                      <p className="text-xs leading-snug text-muted">
                        {address || "No address — not shown on the map"}
                      </p>
                    </div>
                    <MapBadge location={location} />
                    <TerminalControl
                      location={location}
                      busy={terminalBusyId === location.id}
                      error={terminalError?.locationId === location.id ? terminalError.message : null}
                      onEnable={() => void actions.enableTerminal(location.id)}
                    />
                    <button type="button" onClick={() => actions.startEdit(location.id)} className="btn-ghost shrink-0">
                      Edit
                    </button>
                  </div>
                )}
              </li>
            );
          })}

          {locations.length === 0 && !adding && (
            <li className="rounded-row border border-dashed border-line px-3.5 py-6 text-center text-sm text-muted">
              No locations yet. Without one, walk-in customers can't find this shop on the map.
            </li>
          )}
        </ul>
      )}

      <ShipFromPicker
        shipFrom={shipFrom}
        locations={locations}
        form={shipFromForm}
        onChange={(id) => void actions.setShipFrom(id)}
      />

      {adding && (
        <div className="mt-2.5">
          <LocationEditor
            draft={addDraft}
            errors={addErrors}
            countries={countries}
            countriesStatus={countriesStatus}
            regions={addRegions}
            onChange={actions.setAddField}
            idPrefix="new-location"
            submitLabel="Add location"
            submittingLabel="Adding…"
            busy={addForm.busy}
            onSubmit={() => void actions.add()}
            onCancel={actions.cancelAdd}
          />
        </div>
      )}
    </section>
  );
}
